import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, Bot, Settings as SettingsIcon, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Urls } from '@/lib/navigation';
import { PAPER_ICON_CLASS, PAPER_ICON_STROKE_WIDTH } from './paperShapes';

export type PaperMobileTab = 'chat' | 'agents' | 'settings';

interface TabDef {
  id: PaperMobileTab;
  label: string;
  Icon: LucideIcon;
  isActive: (pathname: string) => boolean;
  go: (navigate: ReturnType<typeof useNavigate>) => void;
}

const TABS: TabDef[] = [
  {
    id: 'chat',
    label: 'Chat',
    Icon: MessageSquare,
    isActive: (p) => p === '/' || p.startsWith('/agent/') || p.startsWith('/demos'),
    go: (navigate) => navigate(Urls.home()),
  },
  {
    id: 'agents',
    label: 'Agents',
    Icon: Bot,
    isActive: (p) => p.startsWith('/agents'),
    go: (navigate) => navigate('/agents'),
  },
  {
    id: 'settings',
    label: 'Settings',
    Icon: SettingsIcon,
    isActive: (p) => p.startsWith('/settings'),
    go: (navigate) => navigate('/settings'),
  },
];

const MOBILE_TAB_BAR_HEIGHT_VAR = '--paper-mobile-tab-bar-height';

export interface PaperMobileTabBarProps {
  /** Force a specific tab as active (overrides path-based detection). */
  active?: PaperMobileTab;
  className?: string;
  devControlSlot?: ReactNode;
}

export const PaperMobileTabBar = (props: PaperMobileTabBarProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const navRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const root = document.documentElement;
    const updateHeight = () => {
      root.style.setProperty(MOBILE_TAB_BAR_HEIGHT_VAR, `${nav.getBoundingClientRect().height}px`);
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(nav);
    window.addEventListener('resize', updateHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateHeight);
      root.style.removeProperty(MOBILE_TAB_BAR_HEIGHT_VAR);
    };
  }, []);

  return (
    <nav
      ref={navRef}
      aria-label="Primary"
      className={cn(
        'relative shrink-0',
        'border-t border-solid border-[var(--hair)]',
        'bg-[var(--surface)]',
        props.className,
      )}
      // Clears the iOS home indicator when the OS exposes it; otherwise a
      // sensible default so the labels don't sit on the screen edge.
      style={{
        paddingBottom: 'var(--paper-mobile-tab-bar-safe-padding)',
        zIndex: 'var(--paper-z-mobile-tab-bar)',
      }}
    >
      <div className="flex items-stretch h-[var(--paper-mobile-tab-bar-row-height)]">
        {TABS.map((t) => {
          const on = props.active ? props.active === t.id : t.isActive(pathname);
          const { Icon } = t;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => t.go(navigate)}
              aria-current={on ? 'page' : undefined}
              className={cn(
                'flex-1 relative flex flex-col items-center justify-center gap-[2px]',
                'border-0 bg-transparent cursor-pointer p-0',
                'transition-colors duration-150',
                on ? 'text-[var(--fg)]' : 'text-[var(--fg-4)] hover:text-[var(--fg-2)]',
              )}
            >
              {on && (
                <span
                  aria-hidden
                  className="absolute top-0 left-[24px] right-[24px] h-[2px] rounded-full bg-[var(--fg)]"
                />
              )}
              <span className="inline-flex items-center justify-center w-[24px] h-[24px]">
                <Icon className={PAPER_ICON_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
              </span>
              <span
                className={cn(
                  'text-[10px] tracking-[0.04em]',
                  on ? 'font-medium' : 'font-normal',
                )}
              >
                {t.label}
              </span>
            </button>
          );
        })}
        {props.devControlSlot && (
          <div
            role="group"
            aria-label="Developer controls"
            className={cn(
              'empty:hidden flex-1 min-w-0',
              'flex items-stretch justify-center',
            )}
          >
            {props.devControlSlot}
          </div>
        )}
      </div>
    </nav>
  );
};
